// Domain types for the frontend

import type { SupportedLangCode } from '@/application/i18n/language'

/** A category term linked to an investigation, labelled for the requested
 * locale (`label`), with its canonical `name` in its own `sourceLocale`. */
export interface InvestigationCategoryTerm {
  id: string
  slug: string
  name: string
  sourceLocale: string
  label: string
}

export type InvestigationSlugs = Partial<Record<SupportedLangCode, string>>

/** Result of `GET /investigations/resolve`: where a public URL segment belongs. */
export interface InvestigationRefResolution {
  id: string
  locale: SupportedLangCode
  /** Current slug for `locale`. */
  slug: string
  slugs: InvestigationSlugs
  /** True only when the requested ref already is `slug`. */
  canonical: boolean
}

export interface Investigation {
  id: string
  name: string
  description: string
  investigationId: string
  /** Current public slug per language (derived from the title displayed in that language). */
  /** Optional: absent from payloads of a backend that predates the slugs. */
  slugs?: InvestigationSlugs
  createdAt: string
  updatedAt: string
  /** The investigation's category terms, labelled for the requested locale. */
  categoryTerms?: InvestigationCategoryTerm[]
  /** @deprecated The terms' labels as plain strings; use `categoryTerms`. */
  categories?: string[]
  author?: string | null
  imageUrl?: string | null
  notes?: string | null
  isActive: boolean
  activeLeadId?: string | null
  activeFactId?: string | null
  authorId?: string | null
  conclusion?: 'true' | 'false' | 'fallacious' | 'completed' | null
  status?: 'pending' | 'active' | 'inactive'
  liveDate?: string | null
  twitchUrl?: string | null
  youtubeUrl?: string | null
  /** The language this investigation was written in. Optional on the
   * frontend type (rather than required, as on the backend) so existing
   * fixtures and mocks don't all need updating for a field with no UI yet
   * — Phase 1 only reads it implicitly through the `?lang=` read path. */
  sourceLocale?: string
}

export interface Lead {
  id: string
  title: string
  investigationId: string
  investigation: Investigation
  description: string
  submitterUsername: string
  notes?: string | null
  imageUrl?: string | null
  createdAt: string
  updatedAt: string
  published: boolean
  conclusion?: 'true' | 'false' | 'fallacious' | 'completed' | null
  position: number
  sourceLocale?: string
}

export interface Fact {
  id: string
  leadId: string
  title: string
  description: string
  /** Optional: a fact may be recorded without a verifiable source. */
  url: string | null
  imageUrl?: string | null
  submittedByUserId?: string | null
  submittedByTwitchUsername?: string | null
  submittedByUsername?: string | null
  createdAt: string
  updatedAt: string
  published: boolean
  position: number
  validatedScore?: string | null
  originalSource?: boolean
  sourceLocale?: string
}

export type ScoreLetter = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

const SCORE_LETTERS_CONST = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const

function isScoreLetter(value: string | null | undefined): value is ScoreLetter {
  return value != null && SCORE_LETTERS_CONST.some((letter) => letter === value)
}

/** Safely coerces a raw string (from an API response) to ScoreLetter, returning null if invalid. */
export function toScoreLetter(value: string | null | undefined): ScoreLetter | null {
  return isScoreLetter(value) ? value : null
}

export interface FactScoreSummary {
  averageScore: ScoreLetter | null
  averageNumeric: number | null
  validatedScore: ScoreLetter | null
  userScore: ScoreLetter | null
  count: number
}

export interface Source {
  id: string
  leadId: string
  title: string
  description: string
  url: string | null
  imageUrl: string | null
  createdAt: string
  updatedAt: string
}

export interface InvestigationReadLead {
  lead: Lead
  /** The lead's original-source fact, mapped to a Source. Null when unset. */
  source: Source | null
  /** Published facts (all facts when the caller is privileged), position-ordered. */
  facts: Fact[]
}

export interface InvestigationRead {
  investigation: Investigation
  leads: InvestigationReadLead[]
  /**
   * Locales the whole investigation subtree is completely translated into,
   * computed server-side by `TranslationAvailabilityService`. Passed
   * straight through to `usePageLangAndHreflang` — never recomputed on the
   * frontend (TRANSLATION-PLAN.md §7, Invariant 1: one computation, read
   * identically by every emitter).
   */
  availableLocales: SupportedLangCode[]
}

// ─── Translations (TRANSLATION-PLAN.md §5, §9 Phase 4) ────────────────────────

/** One translatable field's edit-form state for one locale — the RAW stored
 * translation value (never resolved/fallen-back), keyed by logical field
 * name (`name`/`title`/`description`). Mirrors the backend's
 * `FieldTranslationState`. */
export interface TranslationFieldState {
  value: string | null
  stale: boolean
  /** `false` for a field that participates in translation but never gates
   * locale completeness (fact's `url`) — mirrors the backend's
   * `FieldTranslationState.gating`. The edit form shows such a field as
   * "optional" rather than "missing" when untranslated. */
  gating: boolean
}

/** A taxonomy term (an investigation category) with every label it has.
 * `name` is written in `sourceLocale`; `labels` holds the other locales (never
 * the term's own `sourceLocale`). */
export interface TaxonomyTerm {
  id: string
  vocabulary: 'category'
  slug: string
  name: string
  sourceLocale: SupportedLangCode
  labels: Partial<Record<SupportedLangCode, string>>
}

/** A suggestion from the taxonomy endpoint: the term, its label in the
 * requested locale and how many investigations use it. */
export interface TaxonomyTermOption extends TaxonomyTerm {
  label: string
  usageCount: number
}

/** A row of the category management list: the term and how many investigations use it. */
export interface ManagedTaxonomyTerm extends TaxonomyTerm {
  usageCount: number
}

/** Label of a term in `locale`: its name for its own source locale, else its stored label (or ''). */
export function termLabelIn(term: TaxonomyTerm, locale: SupportedLangCode): string {
  return term.sourceLocale === locale ? term.name : (term.labels[locale] ?? '')
}

/** Response of the find-or-create endpoint: the matching or newly created term. */
export interface CreateTaxonomyTermResult {
  term: TaxonomyTerm
  created: boolean
}

/** One locale's edit-form state for one entity — what feeds a language tab's
 * badge and its field editors. Mirrors the backend's `LocaleTranslationState`. */
export interface LocaleTranslationState {
  locale: SupportedLangCode
  fields: Record<string, TranslationFieldState>
  complete: boolean
  missingFields: number
}

/** One locale's completeness for the translation dashboard. Mirrors the
 * backend's `LocaleAvailability`. */
export interface LocaleAvailability {
  locale: SupportedLangCode
  complete: boolean
  missingFields: number
  staleFields: number
}

/** One row of the translation dashboard — one published investigation's
 * per-locale completeness across its whole subtree. */
export interface TranslationDashboardRow {
  investigationId: string
  name: string
  locales: LocaleAvailability[]
}

/** One entity's translation completeness, on its own and cascaded with its
 * descendants — mirrors the backend's `TranslationTreeNode`. `own` is this
 * entity's fields alone; `aggregate` cascades `own` with every descendant's
 * `aggregate` (a node is complete only once it AND every descendant is). For
 * a leaf (a fact), `aggregate` equals `own`. */
export interface TranslationTreeNode {
  id: string
  label: string
  own: LocaleAvailability[]
  aggregate: LocaleAvailability[]
  hasChildren: boolean
}

/** The investigation drill-down page's data: the investigation's own
 * completeness plus one node per lead, each already cascaded with its own
 * facts. Mirrors the backend's `InvestigationTranslationBreakdown`. */
export interface InvestigationTranslationBreakdown {
  investigation: TranslationTreeNode
  leads: TranslationTreeNode[]
}

/** The lead drill-down page's data: the lead's own completeness plus one
 * node per fact. Mirrors the backend's `LeadTranslationBreakdown`. */
export interface LeadTranslationBreakdown {
  investigationId: string
  investigationName: string
  lead: TranslationTreeNode
  facts: TranslationTreeNode[]
}

/** The `translations` map in an extended `PATCH` payload:
 * `{ [locale]: { [field]: value } }`. An empty string or `null` clears the
 * field back to untranslated; a field key omitted from a locale's object
 * leaves that field unchanged. */
export type TranslationsWritePayload = Record<string, Record<string, string | null>>

/** The `categoryLabels` map in an investigation `PUT` payload:
 * `{ [locale]: { [termId]: label | null } }` — the per-locale label of terms
 * linked to the investigation. A label is global to its term (it applies to
 * every investigation using it); `null` clears it. Only the labels the user
 * edited are sent. */
export type CategoryLabelsWritePayload = Record<string, Record<string, string | null>>

/** Result of importing a per-investigation translation transfer file — mirrors
 * the backend's `TranslationImportResult`. `skipped` lists entities the file
 * referenced that no longer exist (or belong to a different parent);
 * `errors` lists per-entity validation failures, kept separate from skipped
 * items. */
export interface TranslationImportResult {
  investigationsUpdated: number
  leadsUpdated: number
  factsUpdated: number
  skipped: string[]
  errors: string[]
}

export interface User {
  id: string
  username: string
  displayName: string
  role: UserRole
  firstName?: string | null
  lastName?: string | null
  createdAt: string
  updatedAt: string
}

export type UserRole = 'user' | 'streamer' | 'moderator' | 'admin'

export interface UserProfile {
  id: string
  username: string | null
  displayName: string | null
  firstName: string | null
  lastName: string | null
  avatarUrl?: string | null
  biography?: string | null
  role: string
  email?: string | null
  hasPassword?: boolean
  twoFactorEnabled?: boolean
  createdAt: string
}

export interface AdminUser {
  id: string
  username: string | null
  email: string | null
  displayName: string | null
  role: string
  enabled: boolean
  createdAt: string
  avatarUrl: string | null
}

export interface AdminUsersPage {
  users: AdminUser[]
  total: number
  page: number
  limit: number
}

export interface InvestigationTeamMember {
  userId: string
  username: string | null
  displayName: string | null
  role: string
  addedAt: string
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    total: number
    page: number
    limit: number
    hasMore: boolean
  }
}

export interface LeadFilters {
  investigationId?: string
  submitter?: string
  search?: string
}

// Form types
export interface CreateInvestigationFormData {
  name: string
  description: string
  investigationId: string
  imageUrl?: string
  /** Ids of the taxonomy terms linked to the investigation, in display order. */
  categoryTermIds?: string[]
  suggestedByUsername?: string
  /** The language this investigation is written in. Defaults to French on
   * the backend when omitted (TRANSLATION-PLAN.md N1). */
  sourceLocale?: SupportedLangCode
}

export interface CreateLeadFormData {
  title: string
  investigationId: string
  description: string
  imageUrl?: string | null
}

export interface UpdateLeadFormData {
  title?: string
  description?: string
  imageUrl?: string | null
  /** Re-assigns the lead to another user. Streamer/admin only. */
  submitterUsername?: string
  translations?: TranslationsWritePayload
}

// ─── Read-aloud (text-to-speech) ───────────────────────────────────────────

/** One utterance the "Listen to this article" player will speak in order —
 * a paragraph/heading/list-item/blockquote/table-cell-sized chunk of plain
 * text extracted from the article's markdown fields, or a heading/title
 * itself. `sectionId` is the lead/fact id it belongs to (`null` for the
 * investigation's own title/description), reserved for a future
 * table-of-contents sync — v1 does not use it for anything but bookkeeping. */
export interface SpeechSegment {
  id: string
  text: string
  lang: SupportedLangCode
  kind: 'heading' | 'text'
  sectionId: string | null
  words: number
  startWord: number
}

/** Playback state machine for the read-aloud widget. `unsupported` means the
 * widget renders nothing at all (no Web Speech API, or no voice for the
 * article's own language); `loading` covers the up-to-1500ms voice-list
 * wait; `preparing` is the site voice model downloading / the first clip
 * being synthesized. */
export type ReadAloudStatus =
  | 'unsupported'
  | 'loading'
  | 'idle'
  | 'preparing'
  | 'playing'
  | 'paused'
  | 'ended'
  | 'error'

/** One `SpeechSynthesisVoice`, reduced to what the read-aloud voice menu and
 * `voice-selection.ts` need — never the live browser object itself, so it
 * stays serializable and easy to fake in tests. */
export interface TtsVoice {
  /** The browser's own `voiceURI` — stable enough to persist in
   * `read-aloud.store.ts` and look the voice back up next session. */
  id: string
  name: string
  lang: string
  quality: 'high' | 'standard' | 'low'
  isDefault: boolean
}

export interface CreateFactFormData {
  leadId: string
  title: string
  description: string
  /** Optional: the creation form asks for a confirmation when it is left empty. */
  url?: string | null
  imageUrl?: string | null
}

export interface UpdateFactFormData {
  title?: string
  description?: string
  url?: string | null
  imageUrl?: string | null
  /** Re-assigns the fact to another registered user. Streamer/admin only. */
  submittedByUserId?: string | null
  translations?: TranslationsWritePayload
}

// Socket events
export type SocketEventType =
  | 'lead:new'
  | 'lead:delete'
  | 'investigation:new'
  | 'investigation:update'
  | 'investigation:delete'
  | 'error'

export interface SocketEvent<T = unknown> {
  type: SocketEventType
  data: T
  timestamp: string
}

export interface Comment {
  commentId: string
  investigationId: string
  parentId: string | null
  username: string
  userId: string | null
  content: string
  status: 'pending' | 'approved'
  createdAt: string
}

export interface CommentsSummaryRow {
  investigationId: string
  total: number
  pending: number
}

export interface Suggestion {
  suggestionId: string
  title: string
  description?: string | null
  suggestedBy: string
  voteCount: number
  status: 'pending' | 'accepted' | 'rejected'
  createdAt: string
}

export interface PaginatedInvestigations {
  investigations: Investigation[]
  total: number
  page: number
  limit: number
}

export interface CreateCommentFormData {
  content: string
  parentId?: string
}

export interface CreateSuggestionFormData {
  title: string
  description?: string
}

// Lead with investigation grouping
export interface LeadGroup {
  investigation: Investigation
  leads: Lead[]
}

// Dashboard stats
export interface DashboardStats {
  totalInvestigations: number
  totalLeads: number
  recentLeads: Lead[]
  topSubmitters: { username: string; count: number }[]
}

export type HomepageLayout = 'classic' | 'news'

export interface SiteIdentitySettings {
  siteName: string
  siteDescription: string
  activeTheme: string
  publicTheme: string
  homepageLayout: HomepageLayout
  updatedAt: string
}

export interface SocialSettings {
  xUrl: string | null
  blueskyUrl: string | null
  facebookUrl: string | null
  instagramUrl: string | null
  tiktokUrl: string | null
  youtubeUrl: string | null
  twitchUrl: string | null
  discordUrl: string | null
  footerLinks: string[]
  updatedAt: string
}

export interface BotSettings {
  language: 'en' | 'fr'
  youtubeLiveId: string | null
}

// `investigationId` and `link` are mutually exclusive — at most one is set.
export interface BreakingNewsSettings {
  active: boolean
  investigationId: string | null
  link: string | null
  message: string
  updatedAt: string
  /** Current slugs of the linked investigation (empty without a link). */
  investigationSlugs?: InvestigationSlugs
}

export interface HomepageFeed {
  lead: Investigation | null
  headlines: Investigation[]
  latest: Investigation[]
}
